import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  ListGroup,
  ListGroupItem,
  Badge,
  Button,
  Input,
} from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore, OrganizationMembership, getCategoryLabel } from '../../store/organization.store';
import { useCollectionCache } from '../../services/collectionCache.service';
import { AuthUserContext } from '../../components/Session';
import { useToast } from '../../contexts/ToastContext';
import * as ROUTES from '../../constants/routes';

const SelectOrganization: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext) as any;
  const auth = useAuthStore(state => state.auth);

  // Check if we came from sign-in (or have no history to go back to)
  // We use location.state to pass this info, or check history length
  const [canGoBack] = useState(() => {
    // If navigated from sign-in, location.state will have fromSignIn flag
    const state = location.state as { fromSignIn?: boolean } | undefined;
    if (state?.fromSignIn) return false;

    // If history length is <= 2, we likely came from sign-in or direct navigation
    // (1 = direct load, 2 = sign-in -> this page)
    return history.length > 2;
  });

  const storeOrganizations = useOrganizationStore(state => state.organizations);
  const isLoading = useOrganizationStore(state => state.isLoading);
  const setCurrentOrganization = useOrganizationStore(state => state.setCurrentOrganization);
  const clearOrganization = useOrganizationStore(state => state.clearOrganization);
  const { showSuccess } = useToast();

  // Collection cache for loading org-specific collections
  const loadOrgCollections = useCollectionCache(state => state.loadOrgCollections);

  // Use store organizations as primary (updated when user joins new org via invite)
  // Fall back to authUser.organizations for initial load before store is populated
  const organizations: OrganizationMembership[] = useMemo(() => {
    return storeOrganizations.length > 0
      ? storeOrganizations
      : (authUser?.organizations || []);
  }, [storeOrganizations, authUser?.organizations]);

  // Live name resolution: each user array entry carries an `orgName` snapshot
  // that was current when the membership was first added. If the org is later
  // renamed via the Global Organization Manager, that snapshot goes stale.
  // We fetch the canonical names from /organizations once on mount and use
  // them in preference to the snapshot in the picker UI. Falls back to the
  // snapshot if the live fetch hasn't completed (or fails) so we never show
  // a blank entry.
  const [liveNames, setLiveNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!firebase || organizations.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { doc: fbDoc, getDoc: fbGetDoc } = await import('firebase/firestore');
        const results = await Promise.all(
          organizations.map((o) =>
            fbGetDoc(fbDoc(firebase.db, 'organizations', o.orgId))
              .then((snap) => [o.orgId, snap.exists() ? (snap.data() as any)?.name : null] as const)
              .catch(() => [o.orgId, null] as const),
          ),
        );
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const [orgId, name] of results) {
          if (name && typeof name === 'string') next[orgId] = name;
        }
        setLiveNames(next);
      } catch {/* fall back to snapshot names */}
    })();
    return () => { cancelled = true; };
  }, [firebase, organizations]);

  const displayName = (o: OrganizationMembership): string => liveNames[o.orgId] || o.orgName || '';

  // Search + sort: alphabetise by live name, then filter by case-insensitive substring match
  const [searchQuery, setSearchQuery] = useState('');
  const visibleOrganizations = useMemo(() => {
    const sorted = [...organizations].sort((a, b) =>
      displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' })
    );
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((o) => displayName(o).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, searchQuery, liveNames]);

  // Track if we have orgs from authUser (which loads before store finishes)
  const hasOrgsFromAuthUser = authUser?.organizations?.length > 0;

  // Redirect to get-started if no organizations (but wait for data to be available)
  useEffect(() => {
    // Wait for either authUser orgs or store loading to complete
    if (!hasOrgsFromAuthUser && isLoading) return;

    if (organizations.length === 0) {
      history.push(ROUTES.GET_STARTED);
    }
  }, [organizations, history, isLoading, hasOrgsFromAuthUser]);

  // Handle selecting an organization
  const handleSelectOrganization = async (org: OrganizationMembership) => {
    if (!firebase || !auth) return;

    // Set the current organization in the store — prefer the live name from
    // /organizations so post-rename selections show the canonical name.
    const liveName = liveNames[org.orgId] || org.orgName;
    setCurrentOrganization(org.orgId, liveName, org.role, org.orgType || 'artist', org.orgCategory);

    // Check if selected org is a master org and load org-level branding (logoUrl)
    // + delivery overrides (bypassQc / bypassReview).
    try {
      const { doc: fbDoc, getDoc: fbGetDoc } = await import('firebase/firestore');
      const orgDocRef = fbDoc(firebase.db, 'organizations', org.orgId);
      const orgDoc = await fbGetDoc(orgDocRef);
      const orgData = orgDoc.exists() ? orgDoc.data() : null;
      useOrganizationStore.getState().setMasterOrg(orgData?.isMasterOrg === true);
      useOrganizationStore.getState().setCurrentOrgLogo(orgData?.logoUrl || null);
      // Delivery overrides only take effect while the org currently has
      // access (deliveryOverridesEnabled). Access off → neutralise the
      // stored bypass flags so gates/banners behave as if unset.
      const hasDeliveryAccess = orgData?.deliveryOverridesEnabled === true;
      useOrganizationStore.getState().setCurrentOrgDeliveryOverrides({
        bypassQc: hasDeliveryAccess && orgData?.settings?.bypassQcRequirement === true,
        bypassReview: hasDeliveryAccess && orgData?.settings?.bypassReviewRequirement === true,
        bypassQcBeforeReview: hasDeliveryAccess && orgData?.settings?.bypassQcBeforeReview === true,
      });
      useOrganizationStore.getState().setCurrentOrgDeliveryOverridesEnabled(hasDeliveryAccess);
    } catch {
      useOrganizationStore.getState().setMasterOrg(false);
      useOrganizationStore.getState().setCurrentOrgLogo(null);
      useOrganizationStore.getState().setCurrentOrgDeliveryOverrides({ bypassQc: false, bypassReview: false, bypassQcBeforeReview: false });
      useOrganizationStore.getState().setCurrentOrgDeliveryOverridesEnabled(false);
    }

    // Load org-specific collections (clears old org data and loads new org's collections)
    // This is important for chatbot to use correct org data for matching
    loadOrgCollections(firebase, org.orgId);

    // Persist the selection to the user's profile
    try {
      await firebase.setUserCurrentOrg(auth.uid, org.orgId);
    } catch (err) {
      console.error('Failed to persist org selection:', err);
    }

    showSuccess(`Switched to "${liveName}"`);

    // Check for pending review or claim codes
    const pendingReviewCode = sessionStorage.getItem('pendingReviewCode');
    if (pendingReviewCode) {
      sessionStorage.removeItem('pendingReviewCode');
      history.push(`/review/${pendingReviewCode}`);
      return;
    }

    const pendingClaimCode = sessionStorage.getItem('pendingClaimCode');
    if (pendingClaimCode) {
      sessionStorage.removeItem('pendingClaimCode');
      history.push(`/claim/${pendingClaimCode}`);
      return;
    }

    const pendingDeliveryCode = sessionStorage.getItem('pendingDeliveryCode');
    if (pendingDeliveryCode) {
      sessionStorage.removeItem('pendingDeliveryCode');
      history.push(`/delivery/${pendingDeliveryCode}`);
      return;
    }

    // Redirect to the dashboard
    history.push(ROUTES.HOME);
  };

  // Handle going back
  const handleGoBack = () => {
    history.goBack();
  };

  // Handle sign out
  const handleSignOut = async () => {
    if (!firebase) return;

    clearOrganization();
    await firebase.doSignOut();
    history.push(ROUTES.SIGN_IN);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'danger';
      case 'OPERATOR': return 'warning';
      default: return 'info';
    }
  };

  // Show nothing while waiting for data - but can render as soon as authUser orgs are available
  if (!hasOrgsFromAuthUser && isLoading) {
    return null;
  }

  // Will redirect to get-started if no orgs
  if (organizations.length === 0) {
    return null;
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <CardBody>
              <CardTitle tag="h3" className="text-white text-center mb-4">
                Select Organization
              </CardTitle>

              <p className="text-muted text-center mb-4">
                Choose which organization you want to access.
              </p>

              <Input
                type="search"
                placeholder="Search organizations…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mb-3"
                autoFocus
              />

              <ListGroup
                flush
                className="mb-4"
                style={{ maxHeight: '60vh', overflowY: 'auto' }}
              >
                {visibleOrganizations.length === 0 ? (
                  <ListGroupItem className="bg-transparent border-secondary text-muted text-center">
                    No organizations match "{searchQuery}"
                  </ListGroupItem>
                ) : (
                  visibleOrganizations.map((org) => (
                    <ListGroupItem
                      key={org.orgId}
                      className="bg-transparent border-secondary d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectOrganization(org)}
                      tag="button"
                      action
                    >
                      <div>
                        <span className="text-white fw-bold">{displayName(org)}</span>
                        <Badge
                          color={org.orgType === 'artist' ? 'primary' : 'secondary'}
                          className="ms-2"
                          style={{ fontSize: '0.7rem', opacity: 0.8 }}
                        >
                          {getCategoryLabel(org.orgCategory, org.orgType)}
                        </Badge>
                      </div>
                      <Badge color={getRoleBadgeColor(org.role)}>
                        {org.role}
                      </Badge>
                    </ListGroupItem>
                  ))
                )}
              </ListGroup>

              <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              <div className="d-flex justify-content-between">
                {canGoBack ? (
                  <Button color="link" className="text-muted p-0" onClick={handleGoBack}>
                    <i className="tim-icons icon-minimal-left" style={{ marginRight: '4px' }} />
                    Back
                  </Button>
                ) : (
                  <Button color="link" className="text-muted p-0" onClick={handleSignOut}>
                    Sign Out
                  </Button>
                )}
                <Button
                  color="link"
                  className="text-muted p-0"
                  onClick={() => history.push(ROUTES.GET_STARTED)}
                >
                  {/* [ORG-CREATION-LOCKDOWN 2026-06-15] Label trimmed from
                      "Create or Join Organization" — users can no longer
                      self-create orgs; the GetStarted page now only offers
                      the Join flow. */}
                  Join Organization
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SelectOrganization;
